import { Badge } from '@mantine/core';
import { STATUS_LABELS } from '../lib/format';

/**
 * Zentraler Status-Badge (D-M0 v1.2, Brief v2.0 §3): Tint-Pills mit
 * führendem Punkt, doppelt codiert (Farbe PLUS deutsches Label; Notdienst
 * zusätzlich mit Blitz-Icon als Inline-SVG, keine Emojis). Farben kommen
 * ausschließlich aus den --wb-status-Tokens.
 */

type TokenKey =
  | 'draft'
  | 'finalized'
  | 'sent'
  | 'due'
  | 'overdue'
  | 'paid'
  | 'cancelled'
  | 'review'
  | 'adhoc'
  | 'emergency';

interface StatusMeta {
  token: TokenKey;
  bolt?: boolean;
}

/**
 * Beleg-Status (DocStatus), Auftrags-Status (OrderStatus), Kennzeichen.
 * Vollanalyse S1: Die deutschen Labels leben in STATUS_LABELS (lib/format,
 * einzige Quelle) — hier nur noch Farbe/Icon je Status.
 */
const STATUS_META: Record<string, StatusMeta> = {
  // Belege
  DRAFT: { token: 'draft' },
  FINALIZED: { token: 'finalized' },
  SENT: { token: 'sent' },
  PAID: { token: 'paid' },
  CANCELLED: { token: 'cancelled' },
  // Aufträge (Label trägt die Bedeutung, Farbe codiert die Phase)
  ACTIVE: { token: 'finalized' },
  // Wie ACTIVE eingefärbt: beide sind die Phase „läuft" — die Unterscheidung
  // trägt das Label, so wie es der Kommentar oben festlegt (Faruq 2026-08-25).
  IN_PROGRESS: { token: 'finalized' },
  // Faruq 2026-08-29: „Fertig" ist ein ERLEDIGT-Zustand und gehoert damit ins
  // Gruen — wie „Abgerechnet". Bernstein (review) signalisiert Handlungsbedarf
  // und stand hier falsch; unterschieden werden die beiden ueber das Label,
  // wie oben fuer ACTIVE/IN_PROGRESS festgelegt.
  DONE: { token: 'paid' },
  BILLED: { token: 'paid' },
  CLOSED: { token: 'cancelled' },
  // OP-Semantik und Kennzeichen
  DUE: { token: 'due' },
  OVERDUE: { token: 'overdue' },
  REVIEW: { token: 'review' },
  // P2-G §3 On-hold: Kennzeichnung (kein Statuswechsel) — Amber/Attention-Token.
  HOLD: { token: 'review' },
  ADHOC: { token: 'adhoc' },
  EMERGENCY: { token: 'emergency', bolt: true },
  // Versandprotokoll (EmailLog)
  QUEUED: { token: 'due' },
  FAILED: { token: 'overdue' },
  // Angebots-Ausgang der Wiedervorlage (D-W1 QS-5: eine Pill-Sprache)
  QUOTE_OPEN: { token: 'finalized' },
  WON: { token: 'paid' },
  LOST: { token: 'cancelled' },
};

function Bolt() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M13 2 4.5 13.5H11L9.5 22 18 10.5h-6.5L13 2z" />
    </svg>
  );
}

export interface StatusBadgeProps {
  status: string;
  /** Überschreibt das Standard-Label (z. B. Auftrags- vs. Belegkontext). */
  label?: string;
  'data-testid'?: string;
}

export function StatusBadge({ status, label, ...rest }: StatusBadgeProps) {
  const meta = STATUS_META[status] ?? { token: 'draft' as const };
  return (
    <Badge
      className={`wb-status-badge wb-status-${meta.token}`}
      variant="light"
      color="gray"
      leftSection={
        meta.bolt ? <Bolt /> : <span className="wb-status-dot" aria-hidden />
      }
      {...rest}
    >
      {label ?? STATUS_LABELS[status] ?? status}
    </Badge>
  );
}
